/**
 * Icon — single-purpose atom that renders a Material Symbols glyph.
 *
 * Implementation: `<Box component='span' class="material-symbols-outlined">{name}</Box>` with
 * inline styles for the variable font axes (FILL, wght, GRAD, opsz) and a
 * size class. Zero React-side dependencies beyond MUI's palette lookup for
 * the color prop.
 *
 * Why a custom atom (not @mui/icons-material or react-material-symbols):
 *   - matches production loading model (Material Symbols variable font)
 *   - full control of all four variable axes
 *   - no per-icon dependency growth as the symbol set expands
 *
 * Imports the font CSS once globally — see `.storybook/preview.tsx`. In prod,
 * import it once in your app entry.
 *
 * Color resolution reads tokens directly from
 * `tokens/semantic/color.json` (same pattern as Button / Modal /
 * Input). Previously this atom routed colors through MUI's CSS
 * variables (`var(--ads-palette-text-primary)` and friends), but that
 * indirection breaks in nested-provider scopes (Storybook docs pages,
 * react-flow viewports, etc.) where the CssVarsProvider scope is
 * ambiguous and the variable falls back to an unexpected value — this
 * was the source of "icons showing the wrong color" bugs.
 *
 * Context-awareness: when `color !== 'inherit'`, Icon consults the
 * `SurfaceContextProvider` and swaps to `inverse-*` siblings under a
 * light context (e.g. inside a Modal). Same mechanism as Button's
 * `context` prop.
 *
 * @format
 */

import {forwardRef, type CSSProperties} from 'react';

import {Box} from '@mui/material';

import {useTheme, type Theme} from '@mui/material/styles';

import sizingTokens from 'ui/tokens/semantic/sizing.json';
import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

/**
 * Public enums for every controllable axis. Stories, docs tables, and
 * external consumers should import these arrays instead of hand-typing the
 * union literals — control options auto-sync when an enum changes. `as const`
 * keeps the tuples narrow; the types below are derived from them so there
 * is no second source of truth.
 *
 * Icon sizes — five named steps from icon.size in sizing.json (16/20/24/28/32),
 * sourced from observed Figma icon sizes. Pass a raw pixel number to
 * override when the size must match an exact value (e.g. inside a control,
 * where the icon should equal the label's line-height — Button does this
 * automatically).
 */
export const ICON_SIZES = ['xs', 's', 'm', 'l', 'xl'] as const;
export const ICON_WEIGHTS = [100, 200, 300, 400, 500, 600, 700] as const;
export const ICON_GRADES = [-25, 0, 200] as const;
export const ICON_OPTICAL_SIZES = [20, 24, 32, 40, 48] as const;
export const ICON_COLORS = [
  'inherit',
  'primary',
  'good',
  'bad',
  'alert',
  'on-surface',
  'on-surface-muted',
  'disabled',
] as const;

export type IconSize = (typeof ICON_SIZES)[number];
export type IconSizeProp = IconSize | number;
export type IconWeight = (typeof ICON_WEIGHTS)[number];
export type IconGrade = (typeof ICON_GRADES)[number];
export type IconOpticalSize = (typeof ICON_OPTICAL_SIZES)[number];
/** Semantic color roles. `inherit` (default) takes the current text color. */
export type IconColor = (typeof ICON_COLORS)[number];

export interface IconProps {
  /**
   * Ligature name from Material Symbols. Examples: `search`, `check_circle`,
   * `close`, `keyboard_arrow_down`. See https://fonts.google.com/icons for the
   * full set.
   */
  name: string;

  /**
   * Visual size. Pass a named token (`xs`/`s`/`m`/`l`/`xl`, mapped to
   * 16/20/24/28/32 px) or a raw pixel number for exact control.
   * Defaults to `m` (24px).
   */
  size?: IconSizeProp;

  /**
   * FILL variable axis. When true the glyph fills (0 → 1). Many Material
   * Symbols are designed with a paired filled/outlined state — selected
   * tabs, active toggles, etc.
   */
  fill?: boolean;

  /**
   * Weight variable axis (100..700). Default 400. Pair with bolder text
   * when in dense UI.
   */
  weight?: IconWeight;

  /** Optical size axis. Defaults to match `size` prop. */
  opticalSize?: IconOpticalSize;

  /** Grade axis (-25..200). Default 0. Adjusts thickness without changing weight. */
  grade?: IconGrade;

  /** Semantic color role. `inherit` follows current text color. */
  color?: IconColor;

  /**
   * Surface-context override. Default is inherited from the enclosing
   * `<SurfaceContextProvider>` (which itself defaults to `'dark'`), so
   * an Icon inside a Modal automatically picks the `inverse-*`
   * spectrum. Pass this prop only to break out of the inherited
   * context — same pattern as Button.
   *
   * Only meaningful when `color !== 'inherit'`.
   */
  context?: SurfaceContextValue;

  /** Aria label. If omitted the icon is treated as decorative (aria-hidden). */
  ariaLabel?: string;

  /** Pass-through className for one-off overrides. */
  className?: string;

  /** Pass-through style. Merged with computed font-variation-settings. */
  style?: CSSProperties;
}

/** Pulled from icon.size.* in tokens/semantic/sizing.json. Single source of truth. */
const SIZE_PX: Record<IconSize, number> = {
  xs: parseInt(sizingTokens.icon.size.xs.$value),
  s: parseInt(sizingTokens.icon.size.s.$value),
  m: parseInt(sizingTokens.icon.size.m.$value),
  l: parseInt(sizingTokens.icon.size.l.$value),
  xl: parseInt(sizingTokens.icon.size.xl.$value),
};

/**
 * Resolve an `IconColor` role to a concrete hex value, taking the active
 * surface context into account.
 *
 * Dark context → the regular spectrum (`primary.primary`,
 * `surface.on-surface`, …).
 *
 * Light context → the `inverse-*` siblings, designed for modals /
 * popovers / dropdowns. Two roles have no canonical light-context twin:
 * `on-surface-muted` falls back to `disabled.inverse-disabled` and
 * `disabled` reuses the same.
 */
function resolveColor(theme: Theme, role: IconColor): string {
  // No `light ? inverse* : *` switching anymore. SurfaceContextProvider /
  // the styled root sets `data-color-scheme` and MUI's CSS-var
  // cascade resolves every palette read to the active context.
  if (role === 'inherit') return 'inherit';
  const p = theme.palette;
  switch (role) {
    case 'primary':
      return p.primary.main;
    case 'good':
      return p.good.main;
    case 'bad':
      return p.bad.main;
    case 'alert':
      return p.alert.main;
    case 'on-surface':
      return p.text.primary;
    case 'on-surface-muted':
      return p.surface.onSurfaceMuted;
    case 'disabled':
      return p.disabledStates.main;
    default: {
      const _exhaustive: never = role;
      throw new Error(`unhandled case: ${String(_exhaustive)}`);
    }
  }
}

export const Icon = forwardRef<HTMLSpanElement, IconProps>(function Icon(
  {
    name,
    size = 'm',
    fill = false,
    weight,
    opticalSize,
    grade = 0,
    color = 'inherit',
    context: contextProp,
    ariaLabel,
    className,
    style,
  },
  ref,
) {
  const sizePx = typeof size === 'number' ? size : SIZE_PX[size];
  const opsz = opticalSize ?? sizePx;
  // Resolve effective surface context: explicit prop > inherited from
  // SurfaceContextProvider > default 'dark'.
  const context = useSurfaceContext(contextProp);
  const theme = useTheme();

  // Weight inheritance — Material Symbols' `wght` axis maps onto CSS
  // `font-weight`. When the caller doesn't pass an explicit `weight`, we
  // omit `wght` from font-variation-settings entirely and let the regular
  // CSS font-weight cascade drive the axis. That means an icon dropped
  // next to a label inherits the label's weight automatically — e.g. a
  // Button label rendered at 500 pulls every icon inside it to 500
  // without each call site spelling out `weight={500}`.
  //
  // When `weight` IS passed, font-variation-settings includes the
  // explicit value AND fontWeight matches it, so font-variation-settings
  // and CSS font-weight stay in lockstep (some browsers favour one over
  // the other depending on shorthand interactions).
  const axes = [
    `'FILL' ${fill ? 1 : 0}`,
    weight !== undefined ? `'wght' ${weight}` : null,
    `'GRAD' ${grade}`,
    `'opsz' ${opsz}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Box component='span'
      ref={ref}
      data-color-scheme={context}
      className={['material-symbols-outlined', className].filter(Boolean).join(' ')}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      style={{
        fontSize: sizePx,
        lineHeight: 1,
        width: sizePx,
        height: sizePx,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: resolveColor(theme, color),
        fontVariationSettings: axes,
        // Explicit fontWeight: 'inherit' guards against CssBaseline or
        // browser resets that might pin the span to a normal weight. When
        // weight prop IS passed, we mirror it on fontWeight so the two
        // CSS mechanisms can't disagree.
        fontWeight: weight !== undefined ? weight : 'inherit',
        // The font must be loaded by the host app (import 'material-symbols/outlined.css').
        userSelect: 'none',
        ...style,
      }}
    >
      {name}
    </Box>
  );
});
